// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiClipboard, FiPlay, FiStopCircle, FiTrash2 } from "react-icons/fi";
import NoctisStatusDot from "../../components/noctis/NoctisStatusDot";
import { LdsOverviewHero, LdsOverviewPage } from "../../components/lds/LdsOverview";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../components/lds/LdsTable";
import { listNoctisEvents } from "../../api/noctisEvents";

const toIsoStartOfTodayUtc = () => {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
  return start.toISOString();
};

const normalizeSince = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toISOString();
};

const isoToLocalInput = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const localInputToIso = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  // "YYYY-MM-DDTHH:mm" interpreted as local time.
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toISOString();
};

const parseTimestampToIso = (value) => {
  if (value == null) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString();
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
      const numeric = Number(trimmed);
      if (!Number.isFinite(numeric)) return null;
      const ms = numeric < 1e12 ? numeric * 1000 : numeric;
      const d = new Date(ms);
      if (Number.isNaN(d.getTime())) return null;
      return d.toISOString();
    }
    const d = new Date(trimmed);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  }

  return null;
};

const formatNlDateTime = (value) => {
  const iso = parseTimestampToIso(value);
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat("nl-NL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(d);
  } catch {
    return iso;
  }
};

const eventTimestamp = (ev) => {
  const v =
    ev?.timestamp ??
    ev?.time ??
    ev?.ts ??
    ev?.createdAt ??
    ev?.created_at ??
    ev?.at ??
    null;
  return parseTimestampToIso(v);
};

const pickField = (ev, paths) => {
  for (const path of paths) {
    const parts = String(path).split(".");
    let cur = ev;
    let ok = true;
    for (const p of parts) {
      if (cur && typeof cur === "object" && p in cur) cur = cur[p];
      else {
        ok = false;
        break;
      }
    }
    if (!ok) continue;
    if (cur == null) continue;
    const asString = String(cur).trim();
    if (asString) return asString;
  }
  return "";
};

const minimalFieldsFromEvent = (ev) => {
  const eventType = pickField(ev, ["eventType", "type", "event", "name"]);
  const externalObjectId = pickField(ev, [
    "externalObjectId",
    "external_object_id",
    "objectId",
    "object_id",
    "payload.externalObjectId",
    "payload.external_object_id",
  ]);
  const childId = pickField(ev, ["childId", "child_id", "payload.childId", "payload.child_id"]);
  const httpStatus = pickField(ev, ["httpStatus", "http_status", "status", "response.status", "payload.httpStatus"]);
  const errorMessage = pickField(ev, [
    "errorMessage",
    "error_message",
    "message",
    "error",
    "payload.errorMessage",
    "payload.error_message",
  ]);

  return {
    ts: eventTimestamp(ev) || "",
    eventType,
    externalObjectId: externalObjectId || childId,
    httpStatus,
    errorMessage,
  };
};

export default function NoctisEventsLog() {
  const { t } = useTranslation();
  const [running, setRunning] = useState(true);
  const [since, setSince] = useState(toIsoStartOfTodayUtc);
  const [nextSince, setNextSince] = useState("");
  const [pollSeconds, setPollSeconds] = useState(30);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const tailRef = useRef(null);
  const timerRef = useRef(null);
  const inflightRef = useRef(false);

  const effectiveSince = useMemo(() => normalizeSince(nextSince || since), [nextSince, since]);

  const schedule = (fn, delayMs) => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(fn, delayMs);
  };

  const fetchOnce = async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const data = await listNoctisEvents({ since: effectiveSince });
      const items = Array.isArray(data) ? data : data?.items ?? data?.events ?? data?.data ?? [];
      setEvents((prev) => {
        const next = [...prev, ...items];
        return next.slice(-1500);
      });

      const serverNextSince = data?.nextSince || data?.next_since || null;
      if (serverNextSince) {
        setNextSince(String(serverNextSince));
      } else {
        const timestamps = items.map(eventTimestamp).filter(Boolean);
        const maxTs = timestamps.sort().slice(-1)[0] || null;
        if (maxTs) setNextSince(maxTs);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || String(e));
    } finally {
      inflightRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!running) return undefined;
    if (typeof window === "undefined") return undefined;

    const tick = async () => {
      await fetchOnce();
      if (!running) return;
      const delay = Math.max(1000, Number(pollSeconds) * 1000);
      schedule(tick, delay);
    };

    schedule(tick, 50);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, effectiveSince, pollSeconds]);

  useEffect(() => {
    if (!autoScroll) return;
    tailRef.current?.scrollIntoView?.({ block: "end" });
  }, [events, autoScroll]);

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FiClipboard}
        eyebrow={t("noctis.menu", { defaultValue: "Noctis" })}
        title={t("noctis.events.title", { defaultValue: "Noctis logs" })}
        description={t("noctis.events.description", { defaultValue: "Bekijk events en logging van de koppeling." })}
        actions={<NoctisStatusDot />}
      />
      <div className="space-y-6 text-gray-900 dark:text-gray-100">
        <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  {t("noctis.events.since", { defaultValue: "Sinds" })}
                </label>
                <input
                  type="datetime-local"
                  value={isoToLocalInput(since)}
                  onChange={(e) => {
                    setSince(localInputToIso(e.target.value));
                    setNextSince("");
                  }}
                  className="w-[360px] max-w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  step={60}
                />
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  {t("noctis.events.poll", { defaultValue: "Poll (s)" })}
                </label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={pollSeconds}
                  onChange={(e) => setPollSeconds(Number(e.target.value || 0))}
                  className="w-24 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                />

                <label className="ml-2 inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={autoScroll}
                    onChange={(e) => setAutoScroll(e.target.checked)}
                  />
                  {t("noctis.events.autoscroll", { defaultValue: "Auto-scroll" })}
                </label>
              </div>

              {nextSince && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t("noctis.events.next_since", { defaultValue: "Next since:" })} {normalizeSince(nextSince)}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setRunning(true)}
                disabled={running}
                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FiPlay />
                {t("common.start", { defaultValue: "Start" })}
              </button>
              <button
                type="button"
                onClick={() => setRunning(false)}
                disabled={!running}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FiStopCircle />
                {t("common.stop", { defaultValue: "Stop" })}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEvents([]);
                  setNextSince("");
                  setError(null);
                }}
                className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 hover:text-indigo-900 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200"
              >
                <FiTrash2 />
                {t("common.clear", { defaultValue: "Clear" })}
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-4 rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-100">
              {error}
            </div>
          )}
        </div>

        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
          <div className="mb-3 flex items-center justify-between gap-4">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              {t("noctis.events.count", { defaultValue: "Events:" })} {events.length}
              {loading ? ` — ${t("common.loading", { defaultValue: "Loading..." })}` : ""}
            </div>
          </div>

          <LdsTableCard>
            <LdsTableScroll className="max-h-[620px]">
              {events.length === 0 ? (
                <div className="p-4 text-sm text-gray-400">
                  {t("noctis.events.empty", { defaultValue: "No events yet." })}
                </div>
              ) : (
                <LdsTable>
                  <LdsTableHead>
                    <LdsTableRow>
                      <LdsTableHeadCell className="w-[190px]">timestamp</LdsTableHeadCell>
                      <LdsTableHeadCell className="w-[170px]">eventType</LdsTableHeadCell>
                      <LdsTableHeadCell className="w-[210px]">externalObjectId/childId</LdsTableHeadCell>
                      <LdsTableHeadCell className="w-[100px]">httpStatus</LdsTableHeadCell>
                      <LdsTableHeadCell>errorMessage</LdsTableHeadCell>
                    </LdsTableRow>
                  </LdsTableHead>
                  <LdsTableBody>
                    {events.map((ev, idx) => {
                      const row = minimalFieldsFromEvent(ev);
                      const key = `${idx}-${row.ts || ""}`;
                      const hasError = Boolean(row.errorMessage) && row.errorMessage.toLowerCase() !== "ok";
                      return (
                        <LdsTableRow key={key}>
                          <LdsTableCell className="font-mono text-gray-700 dark:text-gray-200" title={row.ts || ""}>
                            {row.ts ? formatNlDateTime(row.ts) : "-"}
                          </LdsTableCell>
                          <LdsTableCell>{row.eventType || "-"}</LdsTableCell>
                          <LdsTableCell className="font-mono">{row.externalObjectId || "-"}</LdsTableCell>
                          <LdsTableCell className="font-mono">{row.httpStatus || "-"}</LdsTableCell>
                          <LdsTableCell className={hasError ? "text-rose-700 dark:text-rose-200" : undefined}>
                            {row.errorMessage || "-"}
                          </LdsTableCell>
                        </LdsTableRow>
                      );
                    })}
                  </LdsTableBody>
                </LdsTable>
              )}
              <div ref={tailRef} />
            </LdsTableScroll>
          </LdsTableCard>
        </div>
      </div>
    </LdsOverviewPage>
  );
}
